import { get, post, apiClient } from './client';
import type {
  AdminSubscriptionListResponse,
  AdminSubscription,
  ListAdminSubscriptionsParams,
  SubscriptionRenewalsResponse,
} from '@/types/admin-subscription';

export async function listAdminSubscriptions(
  params?: ListAdminSubscriptionsParams
): Promise<AdminSubscriptionListResponse> {
  const queryParams: Record<string, string> = {};
  if (params?.merchantId) queryParams.merchantId = params.merchantId;
  // Legacy single serviceId (read-compat) → the BFF folds it into the service axis.
  if (params?.serviceId) queryParams.serviceId = params.serviceId;
  if (params?.status) queryParams.subscriptionStatus = params.status;
  // Spec 410: derived multi-value filter axes + substring + renewalCount.
  if (params?.service) queryParams.service = params.service;
  if (params?.operator) queryParams.operator = params.operator;
  if (params?.statuses) queryParams.status = params.statuses;
  if (params?.subId) queryParams.subId = params.subId;
  if (params?.renewals) queryParams.renewals = params.renewals;
  if (params?.limit) queryParams.limit = String(params.limit);
  if (params?.cursor) queryParams.cursor = params.cursor;
  return get<AdminSubscriptionListResponse>('/admin/subscriptions', queryParams);
}

/**
 * Get a single subscription by ID (O(1) Punkt-Lookup, tenant-checked im BFF).
 * Spec 481: `noRetry` fuer die Kaskaden-Stufe „Subscription-Voll-ID" — ein
 * 504-Retry-Dreifach wuerde die Stop-on-Error-Politik der Suche unterlaufen
 * (Muster getAdminTransaction, #479). Default-Verhalten (Detail-Query-Hook)
 * bleibt unveraendert retry-behaftet.
 */
export async function getAdminSubscription(
  subscriptionId: string,
  opts?: { noRetry?: boolean },
): Promise<AdminSubscription> {
  const endpoint = `/admin/subscriptions/${encodeURIComponent(subscriptionId)}`;
  if (opts?.noRetry) {
    return apiClient<AdminSubscription>(endpoint, { method: 'GET', noRetry: true });
  }
  return get<AdminSubscription>(endpoint);
}

// Spec 0222: Charge-Historie (Create + Renewals) via BFF-Proxy (Contract v2.8)
export async function getSubscriptionRenewals(
  subscriptionId: string
): Promise<SubscriptionRenewalsResponse> {
  return get<SubscriptionRenewalsResponse>(
    `/admin/subscriptions/${encodeURIComponent(subscriptionId)}/renewals`
  );
}

export async function terminateSubscription(
  subscriptionId: string,
  reason?: string
): Promise<AdminSubscription> {
  return post<AdminSubscription>(
    `/admin/subscriptions/${encodeURIComponent(subscriptionId)}/terminate`,
    reason ? { reason } : undefined
  );
}
